import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { differenceInDays, format, parse } from 'date-fns';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { formatEpgDate, programIsFullyWatchable } from '@jwp/ott-common/src/utils/epg';
import useAutoRerender from '@jwp/ott-hooks-react/src/useAutoRerender';
import { formatLocalizedTime } from '@jwp/ott-common/src/utils/formatting';

import Spinner from '../../Spinner/Spinner';
import useBreakpoint from '../../../hooks/useBreakpoint';
import IconButton from '../../IconButton/IconButton';
import Icon from '../../Icon/Icon';

import styles from './EpgSchedule.module.scss';
import useEpgKeyboardControl, { type FocusState } from './useEpgKeyboardControl';
import {
  CHANNEL_HEIGHT,
  CHANNEL_HEIGHT_WITH_SPACING,
  CHANNEL_WIDTH,
  HOUR_OFFSET,
  HOURS_BEFORE,
  HOURS_TOTAL,
  MS_PER_HOUR,
  MS_PER_MINUTE,
  VISIBLE_HOURS,
} from './constants';

type Props = {
  channels: EpgChannel[];
  onProgramClick: (program: EpgProgram) => void;
  selectedDayOffset: number;
  isFetching: boolean;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  fetchPreviousPage: () => void;
  fetchNextPage: () => void;
  loadedDays: string[];
};

function dayForColumn(columnIndex: number, timelineStart: number): string {
  const time = timelineStart + columnIndex * MS_PER_HOUR;
  return format(time, 'MM-dd-yyyy');
}

const EpgControl = React.memo(({ scrollLeft, scrollRight }: { scrollLeft: () => void; scrollRight: () => void }) => {
  const { t } = useTranslation('common');

  return (
    <div className={styles.controlLayer}>
      <IconButton className={styles.leftControl} aria-label={t('common:slide_left')} onClick={scrollLeft}>
        <Icon icon={ChevronLeft} />
      </IconButton>
      <IconButton className={styles.rightControl} aria-label={t('common:slide_right')} onClick={scrollRight}>
        <Icon icon={ChevronRight} />
      </IconButton>
    </div>
  );
});

const EpgNowLine = React.memo(({ left, height, now }: { left: number; height: number; now: number }) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <div className={styles.nowLineLayer}>
      <div className={styles.nowLineLabel} style={{ left }}>
        {formatLocalizedTime(new Date(now), language)}
      </div>
      <div
        className={styles.nowLine}
        style={{
          left,
          height,
        }}
      />
    </div>
  );
});

const EpgChannels = React.memo(({ channels, height, rows }: { channels: EpgChannel[]; height: number; rows: VirtualItem[] }) => {
  return (
    <div className={styles.channelsLayer}>
      <div style={{ position: 'absolute', left: 0, top: 0, height, background: 'var(--body-background-color)', width: CHANNEL_WIDTH }}>
        {rows.map((row) => {
          const channel = channels[row.index];

          return (
            <div
              className={styles.channelRow}
              key={row.key}
              style={{
                height: CHANNEL_HEIGHT,
                top: row.start,
              }}
            >
              <div className={styles.channel}>
                {channel.channelLogoImage && <img src={channel.channelLogoImage} width={32} height={32} alt={channel.title} />}
                <div className={styles.channelTitle}>{channel.title}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

const EpgDateHeader = React.memo(({ visibleDay }: { visibleDay: string }) => {
  return (
    <div className={styles.dateHeaderLayer}>
      <div className={styles.dateHeader}>{formatEpgDate(parse(visibleDay, 'MM-dd-yyyy', new Date()))}</div>
    </div>
  );
});

const EpgRows = React.memo(
  ({
    channels,
    rows,
    columns,
    width,
    height,
    timelineStart,
    pxPerHour,
    onProgramClick,
    focusState,
    isFetching,
  }: {
    channels: EpgChannel[];
    rows: VirtualItem[];
    columns: number[];
    width: number;
    height: number;
    timelineStart: number;
    pxPerHour: number;
    onProgramClick: (program: EpgProgram) => void;
    focusState: FocusState;
    isFetching: boolean;
  }) => {
    return (
      <div
        style={{
          height,
          width,
          position: 'relative',
        }}
      >
        {rows.map((row) => (
          <Channel
            key={channels[row.index].id}
            channel={channels[row.index]}
            top={row.start}
            visibleHours={columns}
            timelineStart={timelineStart}
            onClick={onProgramClick}
            pxPerHour={pxPerHour}
            focusedProgram={focusState.hasFocus && focusState.rowIndex === row.index ? focusState.programId : undefined}
            isFetching={isFetching}
          />
        ))}
      </div>
    );
  },
);

const EpgTimeline = React.memo(
  ({ columns, width, timelineStart, pxPerHour }: { columns: VirtualItem[]; width: number; timelineStart: number; pxPerHour: number }) => {
    const {
      i18n: { language },
    } = useTranslation();

    return (
      <div className={styles.timelineLayer}>
        <div className={styles.timeline} style={{ width }}>
          {columns.map((item) => {
            return (
              <div key={item.key} className={styles.timelineBlock} style={{ left: item.start, width: pxPerHour }}>
                {[0, 15, 30, 45].map((minute) => {
                  const isHour = minute === 0;
                  const hourTime = timelineStart + item.index * MS_PER_HOUR;

                  return (
                    <Fragment key={item.key + String(minute)}>
                      {isHour && <div className={styles.hourLabel}>{formatLocalizedTime(new Date(hourTime), language)}</div>}
                      <div className={classNames(styles.timelineMarker, { [styles.isHour]: isHour })} style={{ left: (minute * pxPerHour) / 60 }} />
                    </Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

const Program = React.memo(
  ({
    width,
    left,
    title,
    channelTitle,
    startTime,
    endTime,
    id,
    isLive,
    isWatchable,
    isActive,
    image,
    onClick,
  }: {
    image?: string;
    width: number;
    left: number;
    title: string;
    channelTitle: string;
    startTime: number;
    endTime: number;
    id: string;
    isLive: boolean;
    isWatchable: boolean;
    isActive: boolean;
    onClick: () => void;
  }) => {
    const {
      t,
      i18n: { language },
    } = useTranslation('epg');
    const showImage = image && width > 200;
    const showTitle = width > 50;
    const translateParams = {
      title,
      channelTitle,
      startTime: formatLocalizedTime(new Date(startTime), language),
      endTime: formatLocalizedTime(new Date(endTime), language),
    };

    return (
      <>
        <div
          className={classNames(styles.program, { [styles.isLive]: isLive, [styles.isDisabled]: !isWatchable, [styles.isActive]: isActive })}
          style={{ left, width }}
          onClick={onClick}
          id={id}
          role="option"
          aria-label={isLive ? t('epg:epg_program_label_live', translateParams) : t('epg:epg_program_label_scheduled', translateParams)}
          aria-selected={isActive}
        >
          {showImage && <img src={image} width={80} height={CHANNEL_HEIGHT - 16 * 2} alt={title} />}
        </div>
        {showTitle ? (
          <div
            className={styles.programText}
            style={{
              left: `max(calc(var(--offset) - ${showImage ? `100px` : '0px'}), ${left}px)`,
              width: `min(${width}px, calc(${width}px - (calc(var(--offset) - ${showImage ? `100px` : '0px'}) - ${left}px)))`,
              paddingLeft: showImage ? 80 + 32 : 16,
            }}
          >
            <div>{title}</div>
          </div>
        ) : (
          <div
            className={styles.programText}
            style={{
              justifyContent: 'center',
              display: width < 20 ? 'none' : 'flex',
              left,
              width,
            }}
          >
            ...
          </div>
        )}
      </>
    );
  },
);

const ProgramSkeleton = ({ timelineStart, fromTime, toTime, pxPerHour }: { timelineStart: number; fromTime: number; toTime: number; pxPerHour: number }) => {
  const pxPerMinute = pxPerHour / 60;
  const left = ((fromTime - timelineStart) / MS_PER_MINUTE) * pxPerMinute + 5;
  const width = ((toTime - fromTime) / MS_PER_MINUTE) * pxPerMinute - 10;

  return <div className={styles.programSkeleton} style={{ left, width, height: CHANNEL_HEIGHT }} />;
};

const Channel = React.memo(
  ({
    channel,
    top,
    visibleHours,
    timelineStart,
    onClick,
    pxPerHour,
    focusedProgram,
    isFetching,
  }: {
    channel: EpgChannel;
    top: number;
    visibleHours: number[];
    timelineStart: number;
    onClick: (program: EpgProgram) => void;
    pxPerHour: number;
    focusedProgram: string | undefined;
    isFetching: boolean;
  }) => {
    const pxPerMinute = pxPerHour / 60;
    const fromHour = visibleHours.at(0) ?? 0;
    const toHour = visibleHours.at(-1) ?? fromHour + 1;

    const fromTime = timelineStart + fromHour * MS_PER_HOUR;
    const toTime = timelineStart + (toHour + 1) * MS_PER_HOUR;

    const programs = channel.programs.filter((program) => {
      const start = new Date(program.startTime).getTime();
      const end = new Date(program.endTime).getTime();

      return end >= fromTime && start <= toTime;
    });

    return (
      <div className={styles.channelRow} style={{ height: CHANNEL_HEIGHT, top }}>
        {isFetching && !programs.length && <ProgramSkeleton fromTime={fromTime} toTime={toTime} timelineStart={timelineStart} pxPerHour={pxPerHour} />}
        {programs.map((program) => {
          const programStart = new Date(program.startTime).getTime();
          const programEnd = new Date(program.endTime).getTime();
          const isLive = programStart <= Date.now() && programEnd > Date.now();
          const isWatchable = isLive || programIsFullyWatchable(program, channel.catchupHours);
          // const isVod = programStart >= Date.now() && programEnd > Date.now();

          const left = ((programStart - timelineStart) / MS_PER_MINUTE) * pxPerMinute + 5;
          const width = ((programEnd - programStart) / MS_PER_MINUTE) * pxPerMinute - 10;

          return (
            <Program
              key={program.id}
              id={program.id}
              title={program.title}
              channelTitle={channel.title}
              startTime={programStart}
              endTime={programEnd}
              image={program.cardImageSmall}
              width={width}
              left={left}
              isActive={focusedProgram === program.id}
              isLive={isLive}
              isWatchable={isWatchable}
              onClick={() => onClick(program)}
            />
          );
        })}
      </div>
    );
  },
);

const placeholderChannels: EpgChannel[] = [...Array(10)].map((_, index) => ({
  id: `placeholder ${index + 1}`,
  demo: false,
  catchupHours: 0,
  title: '',
  description: '',
  backgroundImage: '',
  channelLogoImage: '',
  programs: [],
}));

// t('common:now')
function EpgSchedule({
  channels,
  onProgramClick,
  selectedDayOffset,
  isFetching,
  hasPreviousPage,
  hasNextPage,
  fetchPreviousPage,
  fetchNextPage,
  loadedDays,
}: Props) {
  const scrollViewRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint();
  const { t } = useTranslation('epg');

  // rerender every 15 seconds
  useAutoRerender(15);

  const pxPerHour = useMemo(() => {
    return (window.innerWidth - CHANNEL_WIDTH) / VISIBLE_HOURS;
    // update the size when breakpoint changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakpoint]);

  const renderedChannels = useMemo(() => {
    if (!channels.length && isFetching) return placeholderChannels;
    return channels;
  }, [channels, isFetching]);

  const virtualizer = useVirtualizer({
    count: renderedChannels.length,
    estimateSize: () => CHANNEL_HEIGHT_WITH_SPACING,
    overscan: 2,
    paddingStart: 44 + 16,
    useScrollendEvent: true,
    getScrollElement: () => scrollViewRef.current,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: HOURS_TOTAL,
    getScrollElement: () => scrollViewRef.current,
    estimateSize: () => pxPerHour,
    useScrollendEvent: true,
    overscan: 1,
  });

  // absolute start of the entire EPG timeline
  const timelineStart = useMemo(() => {
    const now = Date.now();
    const date = new Date(now - HOUR_OFFSET * MS_PER_HOUR);
    // round down to nearest full hour
    date.setMinutes(0, 0, 0);
    return date.getTime();
  }, []);

  // real now offset in pixels
  const pxPerMinute = pxPerHour / 60;
  const nowOffsetPx = ((Date.now() - timelineStart) / MS_PER_MINUTE) * pxPerMinute;

  // scroll to the now position when loading OR when the selected day changes
  useLayoutEffect(() => {
    const stableNowOffsetPx = ((Date.now() - timelineStart) / MS_PER_MINUTE) * pxPerMinute;
    const dayOffset = selectedDayOffset * pxPerHour * 24;
    const viewOffset = (scrollViewRef.current?.offsetWidth || 0) / 2;
    columnVirtualizer.scrollToOffset(stableNowOffsetPx + dayOffset - viewOffset);

    // ensure the measurements cache is cleared in case the breakpoint changed
    columnVirtualizer.measure();
  }, [selectedDayOffset, columnVirtualizer, timelineStart, pxPerMinute, pxPerHour]);

  // loading previous/next page logic
  const visibleColumns = columnVirtualizer.getVirtualIndexes();
  const visibleDays = useMemo(() => {
    const days = new Set<string>();

    for (const columnIndex of visibleColumns) {
      days.add(dayForColumn(columnIndex, timelineStart));
    }

    return Array.from(days);
  }, [visibleColumns, timelineStart]);

  useEffect(() => {
    if (isFetching) return;

    for (const visibleDay of visibleDays) {
      if (!loadedDays.includes(visibleDay)) {
        const visibleDate = parse(visibleDay, 'MM-dd-yyyy', new Date());
        const firstLoadedDate = parse(loadedDays[0], 'MM-dd-yyyy', new Date());
        const lastLoadedDate = parse(loadedDays[loadedDays.length - 1], 'MM-dd-yyyy', new Date());

        if (differenceInDays(visibleDate, firstLoadedDate) === -1 && hasPreviousPage) {
          fetchPreviousPage();
        } else if (differenceInDays(visibleDate, lastLoadedDate) === 1 && hasNextPage) {
          fetchNextPage();
        }
        break;
      }
    }
  }, [loadedDays, visibleDays, isFetching, hasPreviousPage, hasNextPage, fetchPreviousPage, fetchNextPage]);

  // calculate the visible day for the date header
  const visibleDay = useMemo(() => {
    const firstColumn = visibleColumns[Math.floor(visibleColumns.length / 2)] || HOURS_BEFORE + VISIBLE_HOURS / 2;
    return dayForColumn(firstColumn, timelineStart);
  }, [visibleColumns, timelineStart]);

  const { focusState, handleFocus, handleBlur, handleKeyDown } = useEpgKeyboardControl({
    channels,
    scrollViewRef,
    rowVirtualizer: virtualizer,
    columnVirtualizer,
    dayOffset: selectedDayOffset,
    onProgramClick,
    getColumnOffset: (timestamp: number) => {
      return ((timestamp - timelineStart) / MS_PER_MINUTE) * pxPerMinute;
    },
  });

  const watchableProgramsCount = useMemo(() => channels.reduce((count, channel) => count + channel.programs.length, 0), [channels]);

  if (watchableProgramsCount === 0 && !isFetching) {
    return (
      <div className={styles.epgContainer}>
        <p className={styles.message}>{t('not_available')}</p>
      </div>
    );
  }

  return (
    <div className={styles.epgContainer}>
      <div
        ref={scrollViewRef}
        className={styles.scrollView}
        onScroll={(event) => {
          const el = event.target as HTMLDivElement;

          requestAnimationFrame(() => {
            el.style.setProperty('--offset', `${el.scrollLeft + CHANNEL_WIDTH}px`);
          });
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        role="application"
        tabIndex={0}
        aria-activedescendant={focusState.programId}
        aria-describedby="epg-instructions"
      >
        <div id="epg-instructions" hidden>
          {t('epg_instructions')}
        </div>
        <EpgDateHeader visibleDay={visibleDay} />
        <EpgTimeline
          columns={columnVirtualizer.getVirtualItems()}
          width={columnVirtualizer.getTotalSize()}
          timelineStart={timelineStart}
          pxPerHour={pxPerHour}
        />
        <EpgNowLine left={nowOffsetPx} height={virtualizer.getTotalSize()} now={Date.now()} />
        <EpgChannels channels={renderedChannels} rows={virtualizer.getVirtualItems()} height={virtualizer.getTotalSize()} />
        <EpgRows
          height={virtualizer.getTotalSize()}
          width={columnVirtualizer.getTotalSize()}
          channels={renderedChannels}
          rows={virtualizer.getVirtualItems()}
          columns={columnVirtualizer.getVirtualIndexes()}
          timelineStart={timelineStart}
          pxPerHour={pxPerHour}
          onProgramClick={onProgramClick}
          focusState={focusState}
          isFetching={isFetching}
        />
      </div>
      <EpgControl
        scrollLeft={() => columnVirtualizer.scrollBy(-pxPerHour, { behavior: 'smooth' })}
        scrollRight={() => columnVirtualizer.scrollBy(pxPerHour, { behavior: 'smooth' })}
      />
      {isFetching && (
        <div className={styles.loadingOverlay} style={{ left: channels.length ? CHANNEL_WIDTH : 0 }}>
          <Spinner />
        </div>
      )}
    </div>
  );
}

export default React.memo(EpgSchedule, shallow);
